import React, { useState, useEffect } from 'react';
import { Card, Button, Icon, Loader, Message, Input, Label } from 'semantic-ui-react';
import axios from 'axios';

const WeeklyReminderPreview = () => {
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [testEmail, setTestEmail] = useState('');
    const [sending, setSending] = useState(false);
    const [sendResult, setSendResult] = useState(null);

    const fetchPreview = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axios.get('/admin/emails/weekly-preview');
            setPreview(res.data);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setLoading(false);
        }
    };

    const sendTest = async () => {
        if (!testEmail || !preview) return;
        setSending(true);
        setSendResult(null);
        try {
            await axios.post('/admin/emails/send-test', {
                type: 'weekly',
                recipientEmail: testEmail,
                customHtml: preview.htmlContent,
                customText: preview.textContent,
                customSubject: preview.subject,
            });
            setSendResult({ success: true, message: `Test email sent to ${testEmail}` });
        } catch (err) {
            setSendResult({ success: false, message: err.response?.data?.error || err.message });
        } finally {
            setSending(false);
        }
    };

    return (
        <div>
            <p style={{ color: '#445566', marginBottom: '20px', fontSize: '14px' }}>
                Weekly reminder emails are AI-generated each Wednesday for users who haven't practiced that week.
                Click "Generate Preview" to see this week's content.
            </p>

            <Card.Group itemsPerRow={2} style={{ marginBottom: '20px' }}>
                <Card style={{ padding: '16px', borderRadius: '8px' }}>
                    <Card.Content>
                        <Card.Header style={{ color: '#2c3e50' }}>
                            <Icon name="magic" style={{ color: '#0884a8' }} /> AI-Generated Content
                        </Card.Header>
                        <Card.Description style={{ marginTop: '8px', color: '#445566' }}>
                            Each week uses a different theme angle. The AI generates fresh, unique email content
                            that feels personal and timely.
                        </Card.Description>
                    </Card.Content>
                </Card>
                <Card style={{ padding: '16px', borderRadius: '8px' }}>
                    <Card.Content>
                        <Card.Header style={{ color: '#2c3e50' }}>
                            <Icon name="calendar" style={{ color: '#0884a8' }} /> Schedule
                        </Card.Header>
                        <Card.Description style={{ marginTop: '8px', color: '#445566' }}>
                            Sent every <strong>Wednesday at 12:00 UTC</strong> to users who practiced in the
                            last 3 weeks but haven't practiced this week.
                        </Card.Description>
                    </Card.Content>
                </Card>
            </Card.Group>

            <Button color="blue" onClick={fetchPreview} loading={loading} disabled={loading}>
                <Icon name="magic" /> Generate Preview
            </Button>

            {error && <Message negative style={{ marginTop: '16px' }} header="Error" content={error} />}

            {preview && (
                <div style={{ marginTop: '24px' }}>
                    <Card fluid style={{ borderRadius: '8px', borderTop: '3px solid #0884a8' }}>
                        <Card.Content>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                <div>
                                    <Label color="blue" style={{ marginRight: '8px' }}>
                                        Week {preview.weekNumber}
                                    </Label>
                                    <Label style={{ backgroundColor: '#f0f0f0' }}>
                                        Theme: {preview.theme}
                                    </Label>
                                </div>
                                <div style={{ fontWeight: 600, color: '#2c3e50' }}>
                                    Subject: {preview.subject}
                                </div>
                            </div>
                            <iframe
                                title="Weekly Email Preview"
                                srcDoc={preview.htmlContent}
                                style={{ width: '100%', height: '600px', border: '1px solid #e0e0e0', borderRadius: '4px' }}
                                sandbox="allow-same-origin"
                            />
                        </Card.Content>
                    </Card>

                    {/* Send Test */}
                    <Card fluid style={{ borderRadius: '8px', marginTop: '16px' }}>
                        <Card.Content>
                            <Card.Header style={{ color: '#2c3e50', marginBottom: '12px' }}>
                                <Icon name="send" style={{ color: '#0884a8' }} /> Send Test Email
                            </Card.Header>
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                <Input
                                    placeholder="email@example.com"
                                    value={testEmail}
                                    onChange={(e) => setTestEmail(e.target.value)}
                                    type="email"
                                    style={{ flex: 1 }}
                                />
                                <Button color="green" onClick={sendTest} loading={sending} disabled={!testEmail || sending}>
                                    <Icon name="send" /> Send
                                </Button>
                            </div>
                            {sendResult && (
                                <Message positive={sendResult.success} negative={!sendResult.success} style={{ marginTop: '12px' }}>
                                    {sendResult.message}
                                </Message>
                            )}
                        </Card.Content>
                    </Card>
                </div>
            )}
        </div>
    );
};

export default WeeklyReminderPreview;
